import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHashHistory } from 'vue-router'

import { getConfig } from '@/common/config'
import * as oauth2Cookie from '@/common/oauth2/oauth2Cookie'
import * as securityHelper from '@/common/securityHelper'
import {
  ACCESS_DENIED_PATH,
  AuthModeEnum,
  SIGNIN_PATH
} from '@/constants'
import { i18n } from '@/langs'
import { catchAllRoute, staticRoutes } from '@/routers/route'
import { useOAuth2Store } from '@/stores/oauth2Store'
import { useSecurityStore } from '@/stores/securityStore'

export function createAppRouter() {
  const router = createRouter({
    history: createWebHashHistory(),
    routes: [...staticRoutes, catchAllRoute] as RouteRecordRaw[]
  })

  let menusLoaded = false

  router.beforeEach(async (to, _from, next) => {
    const authMode = getConfig()?.authMode
    if (authMode !== AuthModeEnum.OAUTH2 && authMode !== AuthModeEnum.LOCAL) {
      return next()
    }

    const securityStore = useSecurityStore()
    const oauth2Store = useOAuth2Store()
    const titleKey = to.meta?.title as string | undefined
    document.title = titleKey ? i18n.global.t(titleKey) : ''

    if (to.path === SIGNIN_PATH) {
      menusLoaded = false
      oauth2Store.signout()
      oauth2Cookie.clear()
      securityStore.clear()
      return next()
    }

    const isAuthenticated = oauth2Store.isAuthenticated()

    if (isAuthenticated && !oauth2Cookie.isValid()) {
      oauth2Store.signout()
      return next({ path: SIGNIN_PATH })
    }

    if (!isAuthenticated && to.path !== SIGNIN_PATH) {
      console.log('User not authenticated, redirecting to signin')

      if (to.meta?.protected && !oauth2Store.getterCurrentRoute) {
        oauth2Store.currentRoute = {
          name: to.name,
          path: to.path,
          query: to.query,
          params: to.params
        }
      }
      return next({ path: SIGNIN_PATH })
    }

    if (isAuthenticated && !menusLoaded) {
      try {
        await securityStore.generateMenus()
        await securityStore.generatePermissions()
        menusLoaded = true
      } catch (e) {
        console.error('Failed to load navigation menus', e)
      }
    }

    if (securityHelper.hasMenuAccess(to)) {
      next()
    } else {
      next({ path: ACCESS_DENIED_PATH })
    }
  })

  return router
}
